import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, Truck, Shield, RefreshCw, Star, ChevronRight, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/ProductCard';
import { products, getBestSellers, getNewArrivals } from '../data/products';
import { categories } from '../data/categories';
import { Input } from '../components/ui/input';

const HomePage = () => {
  const bestSellers = getBestSellers();
  const newArrivals = getNewArrivals();

  const trustFeatures = [
    { icon: Shield, title: 'Safe & Pet-Friendly', description: 'Non-toxic materials safe for all pets' },
    { icon: Truck, title: 'Fast Shipping', description: 'Free shipping across USA on orders $50+' },
    { icon: RefreshCw, title: 'Easy 30-Day Returns', description: 'No questions asked return policy' },
    { icon: ShoppingBag, title: 'Secure Checkout', description: 'Your payment info is always safe' },
  ];

  const reviews = [
    {
      id: 1,
      name: 'Sarah M.',
      rating: 5,
      text: 'My dog absolutely loves this toy. Great quality and very durable! Been using it for months now.',
      pet: 'Max - Golden Retriever'
    },
    {
      id: 2,
      name: 'John D.',
      rating: 5,
      text: 'Perfect toy for my cat. Keeps her entertained for hours. Will definitely buy more!',
      pet: 'Whiskers - Persian Cat'
    },
    {
      id: 3,
      name: 'Emily R.',
      rating: 5,
      text: 'Amazing quality and fast shipping. My puppy is obsessed with the chew toys!',
      pet: 'Bella - Labrador'
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#FFF9F5] to-[#FFE8D4] overflow-hidden" data-testid="hero-section">
        <div className="container-main py-16 md:py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-left"
            >
              <span className="inline-block bg-brand-orange-light text-brand-orange font-medium px-4 py-2 rounded-full text-sm mb-4">
                New Collection Available
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Fun & Durable Toys for{' '}
                <span className="text-brand-orange">Happy Pets</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 max-w-lg">
                Discover engaging toys designed to keep your pets active, healthy, and entertained. Safe materials, endless fun!
              </p>
              <div className="flex flex-wrap gap-4">
                <Button className="btn-primary text-lg" asChild data-testid="hero-shop-btn">
                  <Link to="/category/dog-chew-toys">
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    Shop Pet Toys
                  </Link>
                </Button>
                <Button variant="outline" className="btn-secondary text-lg" asChild data-testid="hero-explore-btn">
                  <Link to="/category/cat-toys">
                    Explore Collections
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </Button>
              </div>

              {/* Hero Highlights */}
              <div className="grid grid-cols-2 gap-4 mt-10">
                {trustFeatures.slice(0, 4).map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                    <feature.icon className="w-5 h-5 text-brand-orange" />
                    <span>{feature.title}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative z-10">
                <img
                  src="https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=700&h=600&fit=crop"
                  alt="Happy dog with toy"
                  className="rounded-3xl shadow-2xl w-full"
                />
              </div>
              {/* Decorative Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-brand-blue rounded-full opacity-20" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-brand-orange rounded-full opacity-20" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="section-padding bg-white" data-testid="categories-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Shop by Category
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Browse our curated collection of pet toys organized by category
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {categories.map((category, idx) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
              >
                <Link
                  to={`/category/${category.slug}`}
                  className="group block relative rounded-3xl overflow-hidden aspect-square"
                  data-testid={`category-${category.slug}`}
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <h3 className="font-heading font-bold text-lg mb-1">{category.name}</h3>
                    <span className="text-sm opacity-80">{category.productCount} Products</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Best Sellers */}
      <section className="section-padding bg-[#FFF9F5]" data-testid="bestsellers-section">
        <div className="container-main">
          <div className="flex justify-between items-center mb-10">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                Best Selling Toys
              </h2>
              <p className="text-gray-600">Our customers' favorite picks</p>
            </div>
            <Link
              to="/category/puzzle-toys"
              className="hidden md:flex items-center text-brand-orange hover:underline font-medium"
              data-testid="view-all-bestsellers"
            >
              View All <ChevronRight className="w-5 h-5 ml-1" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {bestSellers.slice(0, 8).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="section-padding bg-white" data-testid="trust-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Pet Owners Trust Us
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We're committed to providing safe, high-quality toys that both you and your pets will love
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trustFeatures.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-8 rounded-3xl bg-[#FFF9F5] hover:shadow-xl transition-shadow duration-300"
                data-testid={`trust-feature-${idx}`}
              >
                <div className="w-16 h-16 bg-brand-orange-light rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-brand-orange" />
                </div>
                <h3 className="font-heading font-bold text-lg text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* New Arrivals */}
      <section className="section-padding bg-[#FFF9F5]" data-testid="newarrivals-section">
        <div className="container-main">
          <div className="flex justify-between items-center mb-10">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                New Arrivals
              </h2>
              <p className="text-gray-600">Just dropped! Fresh toys for your furry friends</p>
            </div>
            <Link
              to="/category/outdoor-pet-toys"
              className="hidden md:flex items-center text-brand-orange hover:underline font-medium"
              data-testid="view-all-newarrivals"
            >
              View All <ChevronRight className="w-5 h-5 ml-1" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {newArrivals.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section className="section-padding bg-white" data-testid="reviews-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Join thousands of happy pet owners who trust NexaFusion
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {reviews.map((review, idx) => (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-[#FFF9F5] p-8 rounded-3xl"
                data-testid={`review-${review.id}`}
              >
                <div className="flex items-center mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{review.text}"</p>
                <div>
                  <p className="font-heading font-bold text-gray-900">{review.name}</p>
                  <p className="text-sm text-gray-500">{review.pet}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="section-padding bg-brand-orange" data-testid="newsletter-section">
        <div className="container-main">
          <div className="max-w-2xl mx-auto text-center text-white">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Get 10% Off Your First Order
            </h2>
            <p className="mb-8 opacity-90">
              Join our newsletter for pet care tips, exclusive offers, and new arrivals.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" data-testid="newsletter-form">
              <Input
                type="email"
                placeholder="Enter your email"
                className="flex-1 bg-white text-gray-900 border-0 rounded-full px-6"
                data-testid="newsletter-email"
              />
              <Button type="submit" className="bg-gray-900 text-white hover:bg-gray-800 rounded-full px-8" data-testid="newsletter-submit">
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
